import React, { useState, useEffect } from 'react';
import { 
  View, 
  Text, 
  TouchableOpacity, 
  StyleSheet, 
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { Image } from 'expo-image';
import MentionInput, { MentionSuggestionList, MentionUser } from '@/components/MentionInput';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams, useFocusEffect } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import * as Haptics from 'expo-haptics';
import { useFonts, Inter_400Regular, Inter_500Medium, Inter_700Bold } from '@expo-google-fonts/inter';
import { useAuth } from '@/contexts/AuthContext';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useLanguage } from '@/contexts/LanguageContext';

export default function CommunityNewPostScreen() {
  const { t } = useLanguage();
  const router = useRouter();
  const params = useLocalSearchParams<{ 
    type?: string; 
    selectedTopics?: string;
    editMode?: string;
    postId?: string;
    existingContent?: string;
    existingTopics?: string;
  }>();
  const { userProfile } = useAuth();
  
  const isEditMode = params.editMode === 'true';
  const editPostId = params.postId;
  
  const [postContent, setPostContent] = useState(isEditMode ? (params.existingContent || '') : '');
  const [selectedTopics, setSelectedTopics] = useState<string[]>(
    isEditMode && params.existingTopics ? JSON.parse(params.existingTopics) : []
  );
  const [mentionSuggestions, setMentionSuggestions] = useState<MentionUser[]>([]);
  const [mentionIsSearching, setMentionIsSearching] = useState(false);
  const [mentionSelectUser, setMentionSelectUser] = useState<((user: MentionUser) => void) | null>(null);
  
  const isComment = params.type === 'comment';
  const modalTitle = isEditMode 
    ? t('community.newPost.editTitle')
    : (isComment ? t('community.newPost.titleComment') : t('community.newPost.titlePost'));
  const publishText = isEditMode ? t('community.newPost.save') : t('community.newPost.publish');

  // Load selected topics from AsyncStorage when screen regains focus (after returning from topics modal)
  useFocusEffect(
    React.useCallback(() => {
      const loadTopics = async () => {
        try {
          const topicsJson = await AsyncStorage.getItem('selectedTopicsTemp');
          if (topicsJson) {
            const topics = JSON.parse(topicsJson);
            setSelectedTopics(topics);
            // Clear the temp storage after loading
            await AsyncStorage.removeItem('selectedTopicsTemp');
          }
        } catch (e) {
          console.error('Error loading selected topics:', e);
        }
      };
      loadTopics();
    }, [])
  );

  const [fontsLoaded] = useFonts({
    Inter_400Regular,
    Inter_500Medium,
    Inter_700Bold,
  });

  if (!fontsLoaded) {
    return null;
  }

  const handleBack = () => {
    router.back();
  };

  const handleOpenTopics = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    router.push({
      pathname: '/community-topics',
      params: {
        selectedTopics: JSON.stringify(selectedTopics),
      },
    });
  };

  const handlePublish = async () => {
    if (!postContent.trim()) {
      return;
    }

    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);

    if (isEditMode && editPostId) {
      // Store edit data to be processed
      await AsyncStorage.setItem('pendingPostEdit', JSON.stringify({
        postId: editPostId,
        content: postContent.trim(),
        topics: selectedTopics,
      }));
    } else {
      // Store the post data to be published
      await AsyncStorage.setItem('pendingPost', JSON.stringify({
        content: postContent.trim(),
        author: userProfile?.name || t('community.newPost.defaultUser'),
        topics: selectedTopics,
      }));
    }

    // Close modal immediately and let community page handle publishing/updating
    router.back();
  };

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <KeyboardAvoidingView 
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        {/* Modal Header */}
        <View style={styles.modalHeader}>
          <View style={styles.headerContent}>
            <TouchableOpacity onPress={handleBack} style={styles.cancelButtonContainer}>
              <Text style={styles.cancelText}>{t('community.newPost.cancel')}</Text>
            </TouchableOpacity>
            <Text style={styles.modalTitle}>{modalTitle}</Text>
            <TouchableOpacity 
              style={[
                styles.saveHeaderButton, 
                !postContent.trim() && styles.disabledSaveButton
              ]}
              onPress={handlePublish}
              disabled={!postContent.trim()}
            >
              <Text style={styles.saveHeaderButtonText}>{publishText}</Text>
            </TouchableOpacity>
          </View>
        </View>

        <ScrollView 
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          {/* User Info */}
          <View style={styles.userInfo}>
            <View style={styles.userGroup}>
              <View style={styles.userAvatar}>
                {userProfile?.avatar_url ? (
                  <Image 
                    source={{ uri: userProfile.avatar_url }} 
                    style={styles.avatarImage}
                  />
                ) : (
                  <View style={styles.avatarContent}>
                    <Svg width="20" height="20" viewBox="0 0 25 25" fill="none">
                      <Path fillRule="evenodd" clipRule="evenodd" d="M12.4849 15.8462C8.61731 15.8462 5.31445 16.431 5.31445 18.7729C5.31445 21.1148 8.59636 21.7205 12.4849 21.7205C16.3525 21.7205 19.6545 21.1348 19.6545 18.7938C19.6545 16.4529 16.3735 15.8462 12.4849 15.8462Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <Path fillRule="evenodd" clipRule="evenodd" d="M12.4849 12.5059C15.023 12.5059 17.0802 10.4478 17.0802 7.90969C17.0802 5.3716 15.023 3.31445 12.4849 3.31445C9.94682 3.31445 7.8887 5.3716 7.8887 7.90969C7.88015 10.4392 9.92396 12.4973 12.4525 12.5059H12.4849Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </Svg>
                  </View>
                )}
              </View>
              <Text style={styles.userName}>{userProfile?.full_name?.split(' ')[0] || t('community.newPost.defaultUser')}</Text>
              <TouchableOpacity onPress={handleOpenTopics}>
                <Text style={[
                  styles.topicLink, 
                  selectedTopics.length === 0 && styles.topicLinkPlaceholder
                ]}>
                  {selectedTopics.length === 0 
                    ? t('community.newPost.addTopic')
                    : selectedTopics.length === 1 
                    ? (() => { const key = `community.topics.labels.${selectedTopics[0]}`; const label = t(key); return label === key ? selectedTopics[0] : label; })()
                    : t('community.newPost.topicsCount', { count: String(selectedTopics.length) })}
                </Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Text Input */}
          <MentionInput
            style={styles.textInput}
            wrapperStyle={{ flex: 0 }}
            placeholder={t('community.newPost.placeholder')}
            placeholderTextColor="#A1A9B2"
            value={postContent}
            onChangeText={setPostContent}
            multiline
            scrollEnabled={false}
            textAlignVertical="top"
            autoFocus
            externalDropdown
            onSuggestionsChange={(suggestions, isSearching, onSelect) => {
              setMentionSuggestions(suggestions);
              setMentionIsSearching(isSearching);
              setMentionSelectUser(() => onSelect);
            }}
          />

          {/* Mention suggestions — appear right below the typed text */}
          <MentionSuggestionList
            suggestions={mentionSuggestions}
            isSearching={mentionIsSearching}
            onSelect={(user) => mentionSelectUser?.(user)}
          />
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  keyboardView: {
    flex: 1,
  },
  modalHeader: {
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E7EAED',
    height: 56,
    justifyContent: 'center',
  },
  headerContent: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cancelButtonContainer: {
    padding: 4,
  },
  cancelText: {
    color: '#757C85',
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    lineHeight: 14,
  },
  modalTitle: {
    color: '#151515',
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    fontWeight: '700',
    lineHeight: 14,
  },
  saveHeaderButton: {
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  disabledSaveButton: {
    opacity: 0.5,
  },
  saveHeaderButtonText: {
    color: '#015480',
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    fontWeight: '500',
    lineHeight: 14,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 24,
    gap: 24,
    flexGrow: 1,
    paddingBottom: 120,
  },
  userInfo: {
    gap: 8,
  },
  userGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  userAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#DAD8CF',
    borderWidth: 1,
    borderColor: '#A19F97',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarContent: {
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  avatarImage: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  userName: {
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 19.6,
  },
  topicLink: {
    color: '#151515',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 19.6,
  },
  topicLinkPlaceholder: {
    color: '#A1A9B2',
  },
  textInput: {
    color: '#151515',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 19.6,
    minHeight: 20,
    textAlignVertical: 'top',
  },
});
